import * as React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import { StepConnector, StepLabel, styled } from '@mui/material';
import { common } from '@mui/material/colors';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { handleError } from 'utils/helpers';
import { campaignDetail } from 'services/campaign.service';
import { useAppDispatch, useAppSelector } from 'common/hook';
import { setIsLoading } from 'slices/loadingSlice';
import { campaignsResponseFormatter } from 'utils/campaign_helpers';
import { useTranslation } from 'react-i18next';
import { setCampaign } from 'slices/campaignsSlice';
import { toast } from 'react-toastify';
import CampaignConstants from 'pages/admin/campaign/constants';

const ArrowConnector = styled(StepConnector)(({ theme }) => ({
    '& .MuiStepConnector-line': {
        border: 'none',
        position: 'relative',
        width: '100%',
        height: '2px',
        backgroundColor: theme.palette.grey[400],
        '&:after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '100%',
            marginTop: '-5px',
            borderWidth: '5px',
            borderStyle: 'solid',
            borderColor: `transparent transparent transparent ${theme.palette.grey[400]}`,
        },
    },
    '&.Mui-active .MuiStepConnector-line': {
        backgroundColor: theme.palette.primary.main,
        '&:after': {
            borderColor: `transparent transparent transparent ${theme.palette.primary.main}`,
        },
    },
}));

const CustomStepLabel = styled(StepLabel)(({ theme }) => ({
    '&.MuiStepLabel-root': {
        '& .MuiStepLabel-label': {
            color: theme.palette.text.primary,
            fontWeight: '500',
        },
        '& svg': {
            color: common.white,
            fontWeight: '700',
            '& .MuiStepIcon-text ': {
                fill: common.black,
            },
        },
    },

    '&.MuiStepLabel-root .Mui-active': {
        color: theme.palette.primary.main,
        '& svg': {
            color: theme.palette.primary.main,
            '& .MuiStepIcon-text ': {
                fill: common.white,
            },
        },
    },
}));
interface CampaignContainerProps {
    children: React.ReactNode;
}
export const CampaignContainer: React.FC<CampaignContainerProps> = ({
    children,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { id: campaignId } = useParams();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const currentCampaign = useAppSelector(
        (state) => state.campaignState.campaign
    );
    const generateUrl = () => {
        switch (currentCampaign?.kind) {
            case 'normal':
                return `/admin/campaign/design/${campaignId}`;
            case 'page_logic':
                return `/admin/campaign/design/page-logic/${campaignId}`;
            default:
                return `/admin/campaign/design/${campaignId}`;
        }
    };
    const steps = React.useMemo(() => {
        return [
            {
                id: 0,
                label: 'Tổng quan',
                url: `/admin/campaign/overview/${campaignId}`,
            },
            {
                id: 1,
                label: 'Thiết kế chiến dịch',
                url: generateUrl(),
            },
            {
                id: 2,
                label: 'Thu thập dữ liệu',
                url:
                    currentCampaign?.status_client ===
                    CampaignConstants.status.CLIENT_DRAFT
                        ? `/admin/campaign/data-collect/${campaignId}`
                        : `/admin/campaign/data-collect/${campaignId}/survey-active`,
            },
            {
                id: 3,
                label: 'Phân tích dữ liệu',
                url: `/admin/campaign/analytics/${campaignId}`,
            },
        ];
    }, [currentCampaign, campaignId]);

    const fetchData = React.useCallback(async () => {
        try {
            const response = await campaignDetail(Number(campaignId));
            dispatch(setIsLoading(false));
            if (response.success) {
                dispatch(
                    setCampaign(campaignsResponseFormatter(response.data, t))
                );
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            dispatch(setIsLoading(false));
            handleError(error);
        }
    }, [campaignId, dispatch, t]);

    React.useEffect(() => {
        dispatch(setIsLoading(true));
        fetchData();
    }, [dispatch, fetchData]);

    const activeStep = steps.findIndex((step) => {
        const basePath = location.pathname.split('/').slice(0, 4).join('/');
        const baseStepUrl = step.url.split('/').slice(0, 4).join('/');
        return baseStepUrl === basePath;
    });

    const handleStepChange = (stepId: number) => {
        const step = steps.find((s) => s.id === stepId);
        if (step) {
            navigate(step.url);
        }
    };
    return (
        <>
            <Box sx={{ width: '50%', mx: 'auto' }}>
                <Stepper
                    nonLinear
                    activeStep={activeStep}
                    connector={<ArrowConnector />}
                >
                    {steps.map((item, index) => (
                        <Step key={index}>
                            <StepButton
                                disableRipple
                                color="inherit"
                                onClick={() => handleStepChange(item.id)}
                            >
                                <CustomStepLabel>{item.label}</CustomStepLabel>
                            </StepButton>
                        </Step>
                    ))}
                </Stepper>
            </Box>
            <Box sx={{ mt: 1, p: 2 }}>
                <Box>{children}</Box>
            </Box>
        </>
    );
};
